import React, { useState } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client/react/hooks';
import classnames from 'classnames';
import { fromGlobalId } from 'graphql-relay';
import PropTypes from 'prop-types';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import Image from 'lib/images/image-view';

import likePhotoblogMutation, {
    likePhotoBlogMutation as likePhotoBlogMutationType,
    likePhotoBlogMutationVariables,
} from 'four-nets/photoblog/user-blog/mutations/like.graphql';
import unlikePhotoblogMutation, {
    unlikePhotoBlogMutation as unlikePhotoblogMutationType,
    unlikePhotoBlogMutationVariables,
} from 'four-nets/photoblog/user-blog/mutations/unlike.graphql';
import EnhancedText from 'four-nets/ui/enhanced-text/enhanced-text';
import Avatar from 'four-nets/avatar/avatar';
import ComposeMailDialog from 'four-nets/mails/compose-mail/compose-mail-dialog';

import WebIcon from 'four-nets/icons/svg/icon_website.svg';
import PhoneIcon from 'four-nets/icons/svg/icon_phone.svg';
import LikeIcon from 'four-nets/icons/svg/icon_like-2.svg';
import LocationIcon from 'four-nets/icons/svg/icon_place.svg';
import MessageIcon from 'four-nets/icons/svg/icon_message-2.svg';
import FullStarIcon from 'four-nets/icons/svg/icon_star-full.svg';
import RightArrowIcon from 'four-nets/icons/svg/icon_arrow-right.svg';
import OutsideLink from 'four-nets/ui-v2/outside-link/outside-link';

import { VendorFragment } from './vendor.graphql';
import arrowStyles from './svg-arrow.scss';
import defaultStyles from './vendor.scss';

interface Props extends VendorFragment {
    styles: typeof defaultStyles;
}

const Vendor: React.FC<Props> = (props, context) => {
    const { styles, name, album, county, web, phone, cardText, user, userBrandCount, photoblog } = props;
    const albumPhoto =
        album && album.images && album.images.edges && album.images.edges.length && album.images.edges[0]!.node;
    const vendorBaseLink = `/catalog/vendor/${user.username}/`;

    const location = useLocation();

    const [writeMailState, setWriteMailState] = useState<{ open: boolean; to: string[] }>({ open: false, to: [] });

    const [likeFn] = useMutation<likePhotoBlogMutationType, likePhotoBlogMutationVariables>(likePhotoblogMutation, {
        optimisticResponse: {
            likePhotoBlog: {
                __typename: 'LikePhotoBlogPayload',
                photoBlog: {
                    __typename: 'PhotoBlogNode',
                    id: photoblog.id,
                    isLikedByViewer: true,
                    likesCount: (photoblog.likesCount || 0) + 1,
                },
            },
        },
    });

    const [unlikeFn] = useMutation<unlikePhotoblogMutationType, unlikePhotoBlogMutationVariables>(
        unlikePhotoblogMutation,
        {
            optimisticResponse: {
                unlikePhotoBlog: {
                    __typename: 'UnlikePhotoBlogPayload',
                    photoBlog: {
                        __typename: 'PhotoBlogNode',
                        id: photoblog.id,
                        isLikedByViewer: false,
                        likesCount: (photoblog.likesCount || 1) - 1,
                    },
                },
            },
        }
    );

    const onWriteMailClick = () => {
        const { user } = props;

        if (context?.authCheck) {
            context?.authCheck(() => setWriteMailState({ open: true, to: [user.username] }));
        } else {
            setWriteMailState({ open: true, to: [user.username] });
        }
    };

    const onClose = () => {
        setWriteMailState({ open: false, to: [] });
    };

    return (
        <>
            <ComposeMailDialog {...writeMailState} onClose={onClose} />
            <div className={styles.container}>
                {albumPhoto ? (
                    <div className={styles.logo}>
                        <Image
                            style={{ image: styles.imageImage }}
                            aspectFill={true}
                            sizes={{ xs: 250 }}
                            src={albumPhoto!.photoFile!.src}
                            srcSet={albumPhoto!.photoFile!.srcset || undefined}
                        />
                    </div>
                ) : (
                    <div className={styles.logoPlaceholder}></div>
                )}
                <div className={styles.infoContainer}>
                    <div className={styles.header}>
                        {user && user.profile && user.profile.avatar ? (
                            <div className={styles.avatarWrapper}>
                                <Avatar style={{ avatar: styles.avatar }} user={user} />
                            </div>
                        ) : null}
                        <div className={styles.headerInfoDiv}>
                            <Link className={styles.vendorName} to={vendorBaseLink}>
                                {name}
                            </Link>
                            <div className={styles.headerAdditionalInfoContainer}>
                                {county ? (
                                    <Link
                                        to={`${location.pathname}?county=${fromGlobalId(county.id).id}`}
                                        className={styles.headerAdditionalInfoDiv}
                                    >
                                        <SVGIcon style={{ image: styles.icon }} src={LocationIcon} />
                                        {county.name}
                                    </Link>
                                ) : null}
                                <button
                                    onClick={() => {
                                        if (photoblog.isLikedByViewer) {
                                            unlikeFn({ variables: { id: photoblog.id } });
                                        } else {
                                            likeFn({ variables: { id: photoblog.id } });
                                        }
                                    }}
                                    className={styles.headerAdditionalInfoDiv}
                                >
                                    <SVGIcon style={{ image: styles.icon }} src={LikeIcon} />
                                    <span className={styles.likes}>{(photoblog && photoblog.likesCount) || 0}</span>
                                    {photoblog && photoblog.isLikedByViewer ? (
                                        <FormattedMessage id='UNLIKE' />
                                    ) : (
                                        <FormattedMessage id='LIKE' />
                                    )}
                                </button>
                                <Link
                                    to={{
                                        pathname: vendorBaseLink,
                                        state: { scrollToUserBrands: true },
                                    }}
                                    className={classnames(styles.headerAdditionalInfoDiv, styles.weddingOnly)}
                                >
                                    <SVGIcon style={{ image: styles.icon }} src={FullStarIcon} />
                                    {userBrandCount && userBrandCount > 0 ? (
                                        <FormattedMessage
                                            id='%(counter)s_RECOMMENDATIONS'
                                            values={{ counter: userBrandCount }}
                                        />
                                    ) : (
                                        <FormattedMessage id='RECOMMEND' />
                                    )}
                                </Link>
                            </div>
                        </div>
                    </div>
                    <div>
                        <EnhancedText followLink={true}>{cardText}</EnhancedText>
                    </div>
                    <Link to={vendorBaseLink} className={styles.vendorInfoLink}>
                        <FormattedMessage id='SHOW_VENDOR_PROFILE' />
                        <SVGIcon style={{ image: arrowStyles.icon }} src={RightArrowIcon} />
                    </Link>
                    <div className={styles.contactContainer}>
                        <button className={styles.contactLinkDiv} onClick={onWriteMailClick}>
                            <SVGIcon style={{ image: styles.icon }} src={MessageIcon} />
                            <span className={styles.contactInfo}>
                                <FormattedMessage id='SEND_PRIVATE_MESSAGE_SHORT'>
                                    {msg => <span className={styles.sendMessageShort}>{msg}</span>}
                                </FormattedMessage>
                                <FormattedMessage id='SEND_PRIVATE_MESSAGE'>
                                    {msg => <span className={styles.sendMessageLong}>{msg}</span>}
                                </FormattedMessage>
                            </span>
                        </button>
                        {phone ? (
                            <span className={styles.contactDiv}>
                                <SVGIcon style={{ image: styles.icon }} src={PhoneIcon} />
                                <span className={styles.contactInfo}>{phone}</span>
                            </span>
                        ) : null}
                        {web ? (
                            <span className={styles.contactLinkDiv}>
                                <SVGIcon style={{ image: styles.icon }} src={WebIcon} />
                                <div className={styles.links}>
                                    <OutsideLink link={web} />
                                </div>
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

Vendor.contextTypes = {
    authCheck: PropTypes.func,
};

export default Styled(defaultStyles)(Vendor);
